import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import QuickEditDialog from "../../../../../components/forms/QuickEditDialog";
import ConfirmationDialog from "../../../../../components/ui/ConfirmationDialog";
import {
  FormDateField,
  FormFieldGrid,
  FormFieldTextProvider,
  FormMoneyField,
  FormSelectField,
  FormTextField,
  FormWarn,
} from "../../../../../components/forms/FormPrimitives";
import {
  secondaryOrderErrorMessage,
  secondaryOrderId,
  secondaryValuationOrdersApi,
} from "../../../../../api/secondaryValuationOrdersApi";
import {
  upsertSecondaryOrder,
  useRefreshSecondaryOrders,
} from "../../../../../components/appraisalOrders/secondaryOrderUi";
import { FORM, px } from "../../../../../theme/formStyles";

/* ─────────────────────────────────────────────
   The three actions on an outside-party (secondary) order, each one modal:
     submitted — "Mark placed": the vendor's order number (+ note)
     received  — "Mark received": the vendor's figures, the report, a note
     cancel    — confirm, with an optional reason
   `action` is { type, order } or null. A 409 (the order moved on under us)
   re-reads the list so the row re-renders with the actions that now apply.
   Also mounted by the Appraisal Queue's Outside orders view, which passes
   `onUpdated` / `onConflict` to keep its own cross-loan list in step.
   ───────────────────────────────────────────── */

export const SECONDARY_ACTION = {
  SUBMITTED: "submitted",
  RECEIVED: "received",
  CANCEL: "cancel",
};

const oidStr = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v.$oid || v._id || null;
  return String(v);
};

const isAppraisalDoc = (doc) => {
  const group = (doc?.docGroup || "").toString().toLowerCase();
  const type = (doc?.docType || "").toString().toLowerCase();
  return group === "appraisal" || type.includes("appraisal");
};

const SecondaryOrderActionDialogs = memo(
  ({ action, dealId, loanDocuments = [], onClose, onUpdated, onConflict }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const refresh = useRefreshSecondaryOrders();

    const type = action?.type || null;
    const order = action?.order || null;
    const orderId = secondaryOrderId(order);

    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Fresh form each time a dialog opens.
    useEffect(() => {
      setForm({});
      setErrorMsg(null);
      setSaving(false);
    }, [type, orderId]);

    // The received report is picked from the loan's appraisal documents
    // (all documents when none are tagged appraisal).
    const docOptions = useMemo(() => {
      const tagged = loanDocuments.filter(isAppraisalDoc);
      return (tagged.length ? tagged : loanDocuments)
        .map((d) => ({
          value: oidStr(d?._id),
          label: d?.file_display_name || d?.docType || "Document",
        }))
        .filter((o) => o.value);
    }, [loanDocuments]);

    // Only touch the loan slice when it holds THIS loan — the Appraisal Queue
    // can act on an order while another loan (or none) is loaded.
    const isLoadedLoan = (getState) =>
      !!dealId && oidStr(getState()?.loanDrilldown?._id) === oidStr(dealId);

    const run = async (call, successText) => {
      if (!orderId || saving) return;
      setSaving(true);
      setErrorMsg(null);
      try {
        const updated = await call();
        if (updated) {
          dispatch((d, getState) => {
            if (isLoadedLoan(getState)) d(upsertSecondaryOrder(updated));
          });
          onUpdated?.(updated);
        }
        enqueueSnackbar(successText, { variant: "success" });
        onClose?.();
      } catch (err) {
        setErrorMsg(secondaryOrderErrorMessage(err));
        if (err?.response?.status === 409) {
          dispatch((d, getState) => {
            if (isLoadedLoan(getState)) refresh(dealId);
          });
          onConflict?.();
        }
      } finally {
        setSaving(false);
      }
    };

    const vendor = order?.provider ? ` with ${order.provider}` : "";
    const errorBlock = errorMsg ? <FormWarn>{errorMsg}</FormWarn> : null;

    return (
      <>
        <QuickEditDialog
          open={type === SECONDARY_ACTION.SUBMITTED}
          title="Mark order placed"
          description={`Record the order number you received when you placed the order${vendor}.`}
          onClose={onClose}
          saving={saving}
          canSave={!!(form.providerOrderId || "").trim()}
          saveText="Mark placed"
          onSave={() =>
            run(
              () =>
                secondaryValuationOrdersApi.markSubmitted(orderId, {
                  providerOrderId: form.providerOrderId.trim(),
                  note: (form.note || "").trim() || undefined,
                }),
              "Order marked as placed",
            )
          }
          hideFieldLabel={false}
          width={520}
          bodySx={{ pt: px(FORM.s.s5) }}
          fieldText="field"
        >
          <FormFieldGrid>
            {errorBlock}
            <FormTextField
              span={6}
              label="Vendor order number"
              required
              value={form.providerOrderId}
              onChange={(v) => set("providerOrderId", v)}
            />
            <FormTextField
              span={6}
              label="Note"
              value={form.note}
              onChange={(v) => set("note", v)}
              placeholder="e.g. Placed through the AMC portal, 5-day turn time"
            />
          </FormFieldGrid>
        </QuickEditDialog>

        <QuickEditDialog
          open={type === SECONDARY_ACTION.RECEIVED}
          title="Mark valuation received"
          description="Enter what the vendor's report says. Every field is optional."
          onClose={onClose}
          saving={saving}
          saveText="Mark received"
          onSave={() =>
            run(
              () =>
                secondaryValuationOrdersApi.markReceived(orderId, {
                  asIsValue: form.asIsValue ?? undefined,
                  arv: form.arv ?? undefined,
                  effectiveDate: form.effectiveDate
                    ? String(form.effectiveDate).slice(0, 10)
                    : undefined,
                  documentOid: form.documentOid || undefined,
                  source: "manual",
                  note: (form.note || "").trim() || undefined,
                }),
              "Valuation marked as received",
            )
          }
          hideFieldLabel={false}
          width={600}
          bodySx={{ pt: px(FORM.s.s5) }}
          fieldText="field"
        >
          <FormFieldGrid>
            {errorBlock}
            <FormMoneyField
              span={3}
              label="As-is value"
              value={form.asIsValue}
              onChange={(v) => set("asIsValue", v)}
            />
            <FormMoneyField
              span={3}
              label="After-repair value"
              value={form.arv}
              onChange={(v) => set("arv", v)}
              helperText="Only when the report gives one."
            />
            <FormDateField
              span={3}
              label="Effective date"
              value={form.effectiveDate}
              onChange={(v) => set("effectiveDate", v)}
            />
            <FormSelectField
              span={3}
              label="Report"
              value={form.documentOid}
              onChange={(v) => set("documentOid", v)}
              options={docOptions}
              placeholder={docOptions.length ? "Select a document" : "No documents on this loan"}
              disabled={!docOptions.length}
              helperText="Upload the report to the loan first to link it here."
            />
            <FormTextField
              span={6}
              label="Note"
              value={form.note}
              onChange={(v) => set("note", v)}
              multiline
              minRows={2}
            />
          </FormFieldGrid>
        </QuickEditDialog>

        <ConfirmationDialog
          open={type === SECONDARY_ACTION.CANCEL}
          title="Cancel this outside valuation order?"
          message={`The order${vendor} stays on the loan's history as cancelled. Let the vendor know separately if it was already placed.`}
          confirmText="Cancel order"
          cancelText="Keep order"
          destructive
          loading={saving}
          onCancel={onClose}
          onConfirm={() =>
            run(
              () =>
                secondaryValuationOrdersApi.cancel(orderId, {
                  reason: (form.reason || "").trim() || undefined,
                }),
              "Order cancelled",
            )
          }
        >
          <FormFieldTextProvider value="field">
            <FormFieldGrid>
              {errorBlock}
              <FormTextField
                label="Reason"
                value={form.reason}
                onChange={(v) => set("reason", v)}
                placeholder="e.g. Internal team picked it up after all"
              />
            </FormFieldGrid>
          </FormFieldTextProvider>
        </ConfirmationDialog>
      </>
    );
  },
);

SecondaryOrderActionDialogs.displayName = "SecondaryOrderActionDialogs";

export default SecondaryOrderActionDialogs;
